"use client";

import type { IconLoadTier } from "@/lib/icon-load-coordinator";
import { useLayoutEffect, useRef, useState } from "react";

const NEAR_ROOT_MARGIN = "400px";

export interface UseIconLoadTierResult {
  ref: React.RefObject<HTMLDivElement | null>;
  /** True when within the near preload margin (400px). */
  inView: boolean;
  tier: IconLoadTier;
  strictVisible: boolean;
  nearViewport: boolean;
}

/** Strict visible (0px) + near (400px) observers for tiered icon loading. */
export function useIconLoadTier(): UseIconLoadTierResult {
  const ref = useRef<HTMLDivElement | null>(null);
  const [strictVisible, setStrictVisible] = useState(false);
  const [nearViewport, setNearViewport] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (typeof IntersectionObserver === "undefined") {
      setStrictVisible(true);
      setNearViewport(true);
      return;
    }

    const strictObserver = new IntersectionObserver(
      ([entry]) => {
        setStrictVisible(entry?.isIntersecting ?? false);
      },
      { rootMargin: "0px" }
    );

    const nearObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setNearViewport(true);
        }
      },
      { rootMargin: NEAR_ROOT_MARGIN }
    );

    strictObserver.observe(element);
    nearObserver.observe(element);

    return () => {
      strictObserver.disconnect();
      nearObserver.disconnect();
    };
  }, []);

  const tier: IconLoadTier = strictVisible ? "visible" : nearViewport ? "near" : "rest";

  return {
    ref,
    inView: nearViewport,
    tier,
    strictVisible,
    nearViewport,
  };
}
